// src/lib/advisorIntakeModel.js
//
// Ticket 071 (advisor reader), reworked in 072. Pure: words the server's allow-listed `intake`
// view (get-advisor-subject) and the pipeline's light `intake` summary for the advisor
// screens. Ticket 072 removed the separate "Shared intake" panel: the case brief renders
// intake clients (src/lib/briefSections.js); what stays here is the gaps list, the footer
// wording and the pipeline chips. No React, no network, no policy: the server already decided what the
// advisor may see (consent, saved version, placeholder strip). This only words it.
//
// Wording and logic are the client intake's own, imported not copied:
//   allAnswersFromFacts / buildReviewAnswers  the client's answers in plain words (the
//                                            move-date placeholder prints as "Lived here
//                                            since before {year}", never as a date)
//   buildResultsView / notAssessed            the estimate state and the "not assessed"
//                                            lines (REVIEW_ROW_LABELS, the impact lines)
//   INTAKE_ASKED_FACTS (via screensAsking)    which open facts the intake really asks

import { allAnswersFromFacts } from './intake/section23Facts'
import { buildReviewAnswers } from './intake/reviewAnswers'
import { buildResultsView } from './intake/resultsModel'
import { screensAsking } from './intake/askedFacts'
import { ordinal } from './briefFormat'

export const ADVISOR_INTAKE_GAPS_TITLE = 'Not provided by the client'
export const ADVISOR_INTAKE_GAP_GROUPS = {
  skipped: 'Skipped income',
  notSure: 'Not sure',
  notAssessed: 'Not assessed',
}
// Gap tags on the "Not provided" card (W10). Stronger first: a screen the estimate left
// out beats a skipped answer, which beats "not sure".
export const ADVISOR_INTAKE_GAP_TAGS = {
  notAssessed: ADVISOR_INTAKE_GAP_GROUPS.notAssessed,
  skipped: 'Skipped',
  notSure: ADVISOR_INTAKE_GAP_GROUPS.notSure,
}
export const ADVISOR_INTAKE_NO_TAX_WORK = 'Saved before Tax work was added; the client can save again.'
export const ADVISOR_INTAKE_UNCONFIRMED_LABEL = 'Rule engine could not confirm:'

const shortDate = (iso) => {
  if (!iso) return null
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

// "Saved Jun 2, 2026 · 2nd save"
export const savedLine = (savedAt, versionNumber) => {
  const date = shortDate(savedAt)
  const version = versionNumber ? `${ordinal(versionNumber)} save` : null
  const saved = date ? `Saved ${date}` : 'Saved'
  return version ? `${saved} · ${version}` : saved
}

export const estimateShownLine = (clientSawEstimate) =>
  (clientSawEstimate ? 'The client was shown the estimate' : 'The client was not shown the estimate')

export const engineLine = (engineVersion) => (engineVersion ? `Calculated with engine ${engineVersion}` : null)

// The header footer for an intake client (D7): one plain line, no Refresh button.
export const intakeFooterLine = (intake) =>
  [savedLine(intake.saved_at, intake.version_number), engineLine(intake.engine_version), estimateShownLine(intake.client_saw_estimate)]
    .filter(Boolean).join(' · ')

// The compact "Not provided by the client" list: one line per item with a tag, duplicates
// merged to the stronger tag. A screen the estimate left out ("Not assessed") absorbs the
// skipped / not-sure rows of the same screen. today: the saved date.
export function buildIntakeGaps(intake, today = new Date()) {
  const facts = intake.facts || {}
  const results = intake.results || {}
  const answers = allAnswersFromFacts(facts, today)
  const ctx = { answers, suggestions: null, dismissed: [] }
  const rows = buildReviewAnswers({ answers, taxYear: intake.tax_year, today })
  const household = facts.household || {}
  const comunidadFact = household.comunidad_autonoma
  const comunidad = comunidadFact && comunidadFact.status === 'known_value' ? comunidadFact.value : null
  const view = buildResultsView({ results, ctx, comunidad })

  const items = []
  const assessedScreens = new Set(view.notAssessed.map((l) => l.screen))
  for (const l of view.notAssessed) {
    items.push({ key: `na:${l.screen}`, label: l.label, tag: ADVISOR_INTAKE_GAP_TAGS.notAssessed, impact: l.impact })
  }
  for (const r of rows) {
    if (r.status === 'skipped') {
      items.push({ key: `sk:${r.id}`, label: r.question, tag: ADVISOR_INTAKE_GAP_TAGS.skipped, impact: null })
    } else if (r.status === 'not_sure' && !assessedScreens.has(r.screen)) {
      items.push({ key: `ns:${r.id}`, label: r.question, tag: ADVISOR_INTAKE_GAP_TAGS.notSure, impact: null })
    }
  }
  return items
}

// ── Pipeline (light summary) ────────────────────────────────────────────────────

// How many screens' worth of asked facts are still open. The pipeline sends compact
// (domain, fact, status) lines; the allow-list of facts the intake really asks lives only
// in the browser bundle, so the count is made here. A skipped income is its own chip.
export function countNotAssessed(openFacts, gaps) {
  const screens = new Set()
  for (const f of [...(openFacts || []), ...(gaps || [])]) {
    if (f.status === 'unknown' && f.domain === 'income' && (f.fact === 'self_income_items' || f.fact === 'spouse_income_items')) continue
    const asking = screensAsking(`${f.domain}.${f.fact}`)
    if (asking.length > 0) screens.add(asking[0])
  }
  return screens.size
}
